import React from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiBuilding, FiShoppingCart, FiTrendingUp, FiDollarSign, FiUsers, FiPackage } = FiIcons;

function AdminOverview() {
  const { prisons, orders, inventory } = useApp();
  
  const totalOrders = orders.length;
  const totalRevenue = orders.reduce((sum, order) => sum + order.total, 0);
  const totalInventoryItems = Object.values(inventory).reduce((sum, items) => sum + items.length, 0);
  const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  // Chart data
  const ordersByPrison = prisons.map(prison => ({
    name: prison.name,
    value: orders.filter(order => order.prisonId === prison.id).length
  }));

  const revenueByPrison = prisons.map(prison => ({
    name: prison.name,
    value: orders
      .filter(order => order.prisonId === prison.id)
      .reduce((sum, order) => sum + order.total, 0)
  }));

  const pieChartOption = {
    title: {
      text: 'Orders by Prison',
      left: 'center'
    },
    tooltip: {
      trigger: 'item'
    },
    legend: {
      orient: 'vertical',
      left: 'left'
    },
    series: [
      {
        name: 'Orders',
        type: 'pie',
        radius: '50%',
        data: ordersByPrison,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  };

  const barChartOption = {
    title: {
      text: 'Revenue by Prison'
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: revenueByPrison.map(item => item.name)
    },
    yAxis: {
      type: 'value'
    },
    series: [
      {
        data: revenueByPrison.map(item => item.value),
        type: 'bar',
        itemStyle: {
          color: '#0ea5e9'
        }
      }
    ]
  };

  const stats = [
    {
      title: 'Total Prisons',
      value: prisons.length,
      icon: FiBuilding,
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    },
    {
      title: 'Total Orders',
      value: totalOrders,
      icon: FiShoppingCart,
      color: 'text-green-600',
      bg: 'bg-green-100'
    },
    {
      title: 'Total Revenue',
      value: `£${totalRevenue.toFixed(2)}`,
      icon: FiDollarSign,
      color: 'text-primary-600',
      bg: 'bg-primary-100'
    },
    {
      title: 'Avg Order Value',
      value: `£${averageOrderValue.toFixed(2)}`,
      icon: FiTrendingUp,
      color: 'text-purple-600',
      bg: 'bg-purple-100'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-government-900 mb-2">
          System Overview
        </h1>
        <p className="text-government-600">
          Monitor the entire AB Orders system across all prisons
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-government-600 mb-1">
                  {stat.title}
                </p>
                <p className="text-2xl font-bold text-government-900">
                  {stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${stat.bg}`}>
                <SafeIcon icon={stat.icon} className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <ReactECharts option={pieChartOption} style={{ height: '400px' }} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <ReactECharts option={barChartOption} style={{ height: '400px' }} />
        </motion.div>
      </div>

      {/* Prison Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <h3 className="text-xl font-bold text-government-900 mb-6">
          Prison Status Overview
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {prisons.map((prison) => {
            const prisonOrders = orders.filter(order => order.prisonId === prison.id);
            const prisonRevenue = prisonOrders.reduce((sum, order) => sum + order.total, 0);
            const prisonItems = inventory[prison.id]?.length || 0;
            
            return (
              <div key={prison.id} className="bg-government-50 p-4 rounded-lg">
                <h4 className="font-semibold text-government-900 mb-2">
                  {prison.name}
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-government-600">Orders:</span>
                    <span className="font-medium">{prisonOrders.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-government-600">Revenue:</span>
                    <span className="font-medium">£{prisonRevenue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-government-600">Menu Items:</span>
                    <span className="font-medium">{prisonItems}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-government-600">Manager:</span>
                    <span className="font-medium text-xs">{prison.manager}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}

export default AdminOverview;